import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import usePageTitle from '@/hooks/usePageTitle';

// SVG components for the buttons
const GoogleLogo: React.FC = () => (
  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleLogo: React.FC = () => (
  <svg width="16" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
    <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const Register: React.FC = () => {


  const query = new URLSearchParams(location.search);
  const emailFromQuery = query.get('email') || '';

  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickName, setNickName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp, signInWithGoogle, signInWithApple } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Calculate display name in real-time
  const displayName = useMemo(() => {
    let name = firstName;
    if (nickName) name += ` "${nickName}"`;
    name += ` ${lastName}`;
    if (suffix) name += ` ${suffix}`;
    return name.trim();
  }, [firstName, lastName, nickName, suffix]);

  // Set page title
  usePageTitle('Register');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    if (!firstName.trim() || !lastName.trim()) {
      return setError('First name and last name are required');
    }

    setLoading(true);

    try {
      await signUp(
        email,
        password,
        firstName,
        lastName,
        displayName,
        nickName,
        suffix
      );
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      if (error instanceof Error) {
        if (error.message.includes('auth/email-already-in-use')) {
          setError('An account with this email already exists. Please try signing in instead.');
        } else {
          setError(error.message);
        }
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (loading) return;

    try {
      setError('');
      setLoading(true);
      await signInWithGoogle('/dashboard');
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Failed to start Google sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (loading) return;

    try {
      setError('');
      setLoading(true);
      await signInWithApple('/dashboard');
    } catch (err) {
      console.error('Apple sign-in error:', err);
      setError('Failed to start Apple sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    maxWidth: '500px',
    margin: '40px auto',
    padding: theme.spacing.LARGE,
    backgroundColor: theme.colors.BACKGROUND,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const inputStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    marginBottom: theme.spacing.MEDIUM,
    border: `1px solid ${theme.colors.INPUT_BORDER}`,
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    backgroundColor: theme.colors.INPUT_BACKGROUND,
    color: theme.colors.INPUT_TEXT,
  };

  const buttonStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
    color: theme.colors.BUTTON_PRIMARY_TEXT,
    border: 'none',
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    cursor: 'pointer',
    marginBottom: theme.spacing.MEDIUM,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontWeight: '500',
    height: '40px',
  };

  const errorStyle = {
    color: theme.colors.ERROR,
    marginBottom: theme.spacing.MEDIUM,
    textAlign: 'center' as const,
    padding: theme.spacing.SMALL,
    backgroundColor: `${theme.colors.ERROR}20`,
    borderRadius: '4px',
  };

  const previewStyle = {
    backgroundColor: theme.colors.SURFACE,
    padding: theme.spacing.MEDIUM,
    borderRadius: '4px',
    marginBottom: theme.spacing.LARGE,
    border: `1px solid ${theme.colors.BORDER}`,
  };

  const sectionStyle = {
    marginBottom: theme.spacing.LARGE,
    padding: theme.spacing.MEDIUM,
    backgroundColor: theme.colors.SURFACE,
    borderRadius: '8px',
    border: `1px solid ${theme.colors.BORDER}`,
  };

  const labelStyle = {
    color: theme.colors.TEXT_SECONDARY,
    fontSize: '0.9em',
    marginBottom: '4px',
    display: 'block',
  };

  const googleButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.GOOGLE_BG,
    color: theme.colors.GOOGLE_TEXT,
    border: `1px solid ${theme.colors.GOOGLE_BORDER}`,
    marginBottom: theme.spacing.LARGE,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const appleButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.APPLE_BG,
    color: theme.colors.APPLE_TEXT,
    marginBottom: theme.spacing.MEDIUM,
  };

  const dividerStyle = {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center' as const,
    margin: `${theme.spacing.LARGE}px 0`,
    color: theme.colors.TEXT,
    opacity: 0.5,
  };

  const dividerLineStyle = {
    flex: 1,
    height: '1px',
    backgroundColor: 'currentColor',
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          input::placeholder {
            color: ${theme.colors.TEXT_SECONDARY} !important;
            opacity: 1;
          }
        `}
      </style>
      <h2
        style={{
          textAlign: 'center',
          color: theme.colors.TEXT,
          marginBottom: theme.spacing.LARGE,
          fontSize: '1.8em',
        }}
      >
        Create an Account
      </h2>

      <button
        onClick={handleGoogleSignIn}
        disabled={loading}
        style={googleButtonStyle}
        type="button"
      >
        <GoogleLogo />
        {loading ? 'Redirecting...' : 'Continue with Google'}
      </button>

      <button
        onClick={handleAppleSignIn}
        disabled={loading}
        style={appleButtonStyle}
        type="button"
      >
        <AppleLogo />
        {loading ? 'Redirecting...' : 'Continue with Apple'}
      </button>

      <div style={dividerStyle}>
        <div style={dividerLineStyle}></div>
        <span style={{ padding: '0 10px' }}>or</span>
        <div style={dividerLineStyle}></div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={sectionStyle}>
          <h3
            style={{
              color: theme.colors.TEXT,
              marginBottom: theme.spacing.MEDIUM,
            }}
          >
            Name Information
          </h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: theme.spacing.MEDIUM,
            }}
          >
            <div>
              <label style={labelStyle}>First Name *</label>
              <input
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Last Name *</label>
              <input
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={inputStyle}
                required
              />
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: theme.spacing.MEDIUM,
            }}
          >
            <div>
              <label style={labelStyle}>Nickname (optional)</label>
              <input
                type="text"
                placeholder='e.g. "Johnny"'
                value={nickName}
                onChange={(e) => setNickName(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Suffix (optional)</label>
              <input
                type="text"
                placeholder="e.g. Jr., Sr., III"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {(firstName || lastName) && (
            <div style={previewStyle}>
              <div
                style={{
                  color: theme.colors.TEXT_SECONDARY,
                  fontSize: '0.9em',
                  marginBottom: '8px',
                }}
              >
                Display Name Preview:
              </div>
              <div
                style={{
                  color: theme.colors.TEXT,
                  fontWeight: 'bold',
                  fontSize: '1.2em',
                  padding: theme.spacing.SMALL,
                  backgroundColor: theme.colors.BACKGROUND,
                  borderRadius: '4px',
                }}
              >
                {displayName}
              </div>
            </div>
          )}
        </div>

        <div style={sectionStyle}>
          <h3
            style={{
              color: theme.colors.TEXT,
              marginBottom: theme.spacing.MEDIUM,
            }}
          >
            Account Information
          </h3>

          <label style={labelStyle}>Email Address *</label>
          <input
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            required
            autoComplete="username"
          />

          <label style={labelStyle}>Password *</label>
          <input
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
            required
            autoComplete="new-password"
          />

          <label style={labelStyle}>Confirm Password *</label>
          <input
            type="password"
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle}
            required
            autoComplete="new-password"
          />
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <button type="submit" disabled={loading} style={buttonStyle}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>

      <div
        style={{
          textAlign: 'center',
          marginTop: theme.spacing.MEDIUM,
          color: theme.colors.TEXT,
        }}
      >
        Already have an account?{' '}
        <Link
          to="/login"
          style={{
            color: theme.colors.LINK,
            textDecoration: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            opacity: 1,
            transition: 'all 0.2s ease',
            padding: `${theme.spacing.SMALL}px ${theme.spacing.SMALL}px`,
            borderRadius: '4px',
            backgroundColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            display: 'inline-block',
            marginLeft: theme.spacing.SMALL,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
            e.currentTarget.style.color = theme.isDark ? theme.colors.LINK : theme.colors.PRIMARY;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
            e.currentTarget.style.color = theme.colors.LINK;
          }}
        >
          Sign in to your account
        </Link>
      </div>
    </div>
  );
};

export default Register;
